import { useTranslations } from "next-intl";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { Input } from "./input";
import { CustomTooltip } from "./tooltip";
import { PaginationInfo } from "@/types";

type PaginationProps = {
   pagination: PaginationInfo;
   handlePageChange: (page: number) => void;
};

type PageSizeControlProps = {
   pageSize: number;
   onPageSizeChange: (pageSize: number) => void;
   min?: number;
   max?: number;
};

export const PageSizeControl = ({
   pageSize,
   onPageSizeChange,
   min = 1,
   max = 500
}: PageSizeControlProps) => {
   const translate = useTranslations("Components.Pagination");
   const [draftPageSize, setDraftPageSize] = useState(String(pageSize));

   useEffect(() => {
      setDraftPageSize(String(pageSize));
   }, [pageSize]);

   useEffect(() => {
      if (!draftPageSize.trim()) return;

      const timer = window.setTimeout(() => {
         applyPageSize();
      }, 600);

      return () => window.clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [draftPageSize]);

   const applyPageSize = () => {
      const parsed = Number(draftPageSize);
      if (!Number.isFinite(parsed)) {
         setDraftPageSize(String(pageSize));
         return;
      }

      const nextPageSize = Math.min(max, Math.max(min, Math.floor(parsed)));
      setDraftPageSize(String(nextPageSize));
      if (nextPageSize !== pageSize) {
         onPageSizeChange(nextPageSize);
      }
   };

   return (
      <CustomTooltip text={translate("maxItemsPerPage", { max })} delayDuration={200}>
         <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
               {translate("itemsPerPage")}
            </span>
            <Input
               type="number"
               min={min}
               max={max}
               value={draftPageSize}
               onChange={(event) => setDraftPageSize(event.target.value)}
               onBlur={() => {
                  if (!draftPageSize.trim()) {
                     setDraftPageSize(String(pageSize));
                     return;
                  }
                  applyPageSize();
               }}
               onKeyDown={(event) => {
                  if (event.key === "Enter") {
                     applyPageSize();
                  }
               }}
               className="h-9 w-20"
               aria-label={translate("itemsPerPage")}
            />
         </div>
      </CustomTooltip>
   );
};

export const Pagination = (props: PaginationProps) => {
   const translate = useTranslations("Components.Pagination");
   const { pagination, handlePageChange } = props;

   return (
      <div className="flex items-center gap-2">
         <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(1)}
            disabled={!pagination.hasPreviousPage}
         >
            <ChevronsLeft className="h-4 w-4" />
         </Button>
         <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={!pagination.hasPreviousPage}
         >
            <ChevronLeft className="h-4 w-4" />
         </Button>
         <span className="text-sm font-medium">
            {translate("pageOfTotal", {
               page: pagination.currentPage,
               totalPages: pagination.totalPages
            })}
         </span>
         <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={!pagination.hasNextPage}
         >
            <ChevronRight className="h-4 w-4" />
         </Button>
         <Button
            variant="outline"
            size="sm"
            onClick={() => handlePageChange(pagination.totalPages)}
            disabled={!pagination.hasNextPage}
         >
            <ChevronsRight className="h-4 w-4" />
         </Button>
      </div>
   );
};
